import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';

// We assume this file is named DivorceTimeline.tsx
// Make sure "jsx": "react" or "react-jsx" is set in tsconfig.json
// Otherwise, TypeScript will raise JSX syntax errors.

// -----------------------------------------------------------------------------
// Utility & Constants
// -----------------------------------------------------------------------------

// Parse user-input like "2" or "2-3" into a numeric (average) value
function parseMonths(str) {
  if (!str) return 0;
  const interval = str.match(/(\d+(?:\.\d+)?)\s*-\s*(\d+(?:\.\d+)?)/);
  if (interval) {
    return (
      (parseFloat(interval[1]) + parseFloat(interval[2])) / 2
    );
  }
  const single = str.match(/(\d+(?:\.\d+)?)/);
  return single ? parseFloat(single[1]) : 0;
}

// Default durations (in months)
const DEFAULT_DOCS_MIN = 0.5;
const DEFAULT_DOCS_MAX = 1;
const ADULTERE_CRUAUTE_EXTRA_MIN = 1;
const ADULTERE_CRUAUTE_EXTRA_MAX = 4;
const EXPERTISE_PENSION_MIN = 3;
const EXPERTISE_PENSION_MAX = 6;
const EVAL_IMMO_MIN = 1;
const EVAL_IMMO_MAX = 3;

// District judiciaire
const DISTRICT_MONTREAL = { min: 12, max: 24 };
const DISTRICT_AUTRE = { min: 9, max: 18 };

// List of countries (La Haye Convention or not)
const hagueCountries = [
  { code: "AL", name: "Albanie" },
  { code: "DE", name: "Allemagne" },
  { code: "AD", name: "Andorre" },
  { code: "AG", name: "Antigua-et-Barbuda" },
  { code: "AR", name: "Argentine" },
  { code: "AM", name: "Arménie" },
  { code: "AU", name: "Australie" },
  { code: "AT", name: "Autriche" },
  { code: "AZ", name: "Azerbaïdjan" },
  { code: "BS", name: "Bahamas" },
  { code: "BB", name: "Barbade" },
  { code: "BY", name: "Bélarus" },
  { code: "BE", name: "Belgique" },
  { code: "BZ", name: "Belize" },
  { code: "BA", name: "Bosnie-Herzégovine" },
  { code: "BW", name: "Botswana" },
  { code: "BR", name: "Brésil" },
  { code: "BG", name: "Bulgarie" },
  { code: "CA", name: "Canada" },
  { code: "CL", name: "Chili" },
  { code: "CN", name: "Chine" }, // Note: Applies to Hong Kong SAR and Macao SAR as well
  { code: "CY", name: "Chypre" },
  { code: "CO", name: "Colombie" },
  { code: "CR", name: "Costa Rica" },
  { code: "HR", name: "Croatie" },
  { code: "DK", name: "Danemark" },
  { code: "DO", name: "République dominicaine" },
  { code: "EG", name: "Égypte" },
  { code: "SV", name: "El Salvador" },
  { code: "EE", name: "Estonie" },
  { code: "US", name: "États-Unis d'Amérique" },
  { code: "FI", name: "Finlande" },
  { code: "FR", name: "France" },
  { code: "GE", name: "Géorgie" },
  { code: "GR", name: "Grèce" },
  { code: "HU", name: "Hongrie" },
  { code: "IN", name: "Inde" },
  { code: "IE", name: "Irlande" },
  { code: "IS", name: "Islande" },
  { code: "IL", name: "Israël" },
  { code: "IT", name: "Italie" },
  { code: "JP", name: "Japon" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "KW", name: "Koweït" },
  { code: "LV", name: "Lettonie" },
  { code: "LT", name: "Lituanie" },
  { code: "LU", name: "Luxembourg" },
  { code: "MW", name: "Malawi" },
  { code: "MT", name: "Malte" },
  { code: "MH", name: "Îles Marshall" },
  { code: "MX", name: "Mexique" },
  { code: "MC", name: "Monaco" },
  { code: "ME", name: "Monténégro" },
  { code: "MA", name: "Maroc" },
  { code: "NL", name: "Pays-Bas" },
  { code: "NI", name: "Nicaragua" },
  { code: "MK", name: "Macédoine du Nord" },
  { code: "NO", name: "Norvège" },
  { code: "PK", name: "Pakistan" },
  { code: "PY", name: "Paraguay" },
  { code: "PH", name: "Philippines" },
  { code: "PL", name: "Pologne" },
  { code: "PT", name: "Portugal" },
  { code: "KR", name: "République de Corée" },
  { code: "MD", name: "République de Moldova" },
  { code: "RO", name: "Roumanie" },
  { code: "GB", name: "Royaume-Uni" },
  { code: "RU", name: "Fédération de Russie" },
  { code: "VC", name: "Saint-Vincent-et-les Grenadines" },
  { code: "SM", name: "Saint-Marin" },
  { code: "RS", name: "Serbie" },
  { code: "SC", name: "Seychelles" },
  { code: "SG", name: "Singapour" },
  { code: "SK", name: "Slovaquie" },
  { code: "SI", name: "Slovénie" },
  { code: "ES", name: "Espagne" },
  { code: "LK", name: "Sri Lanka" },
  { code: "SE", name: "Suède" },
  { code: "CH", name: "Suisse" },
  { code: "TN", name: "Tunisie" },
  { code: "TR", name: "Türkiye" },
  { code: "UA", name: "Ukraine" },
  { code: "VE", name: "Venezuela" },
  { code: "VN", name: "Viet Nam" },
].map((c) => ({ ...c, isSignatory: true }));

const nonHagueCountries = [
  { code: "DZ", name: "Algérie" },
  // { code: "CN", name: "Chine" }, // Now in hagueCountries
  // { code: "IN", name: "Inde" }, // Now in hagueCountries
].map((c) => ({ ...c, isSignatory: false }));

const allCountries = [...hagueCountries, ...nonHagueCountries].sort((a, b) =>
  a.name.localeCompare(b.name)
);

// -----------------------------------------------------------------------------
// Steps Data (Questionnaire + Explanations)
// -----------------------------------------------------------------------------

const stepsData = [
  {
    id: "disclaimer_chevauchement",
    phase: "preparation",
    title: "Note sur le chevauchement des étapes",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Certaines étapes peuvent se dérouler en parallèle.",
    detailedContent: `
      Les durées affichées sont strictement additionnées, 
      mais il est possible que plusieurs étapes s'effectuent simultanément. 
      Par conséquent, la somme totale n'est qu'un indicateur approximatif. 
      Toutefois, nous conservons cette méthode pour simplifier l'estimation. 
    `,
    questions: [],
  },

  // Motif du divorce
  {
    id: "step_motif_divorce",
    phase: "preparation",
    title: "Motif du divorce",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Sélectionnez le motif principal (séparation, adultère, cruauté).",
    detailedContent: `
      Le divorce peut être demandé pour :
      - Rupture après une séparation de 12 mois
      - Adultère
      - Cruauté physique ou mentale
      Note : Pour adultère ou cruauté, vous n'avez pas besoin d'attendre un an, 
      mais la preuve peut être plus complexe.
    `,
    questions: [
      {
        id: "motif_divorce",
        label: "Quel est le motif du divorce ?",
        options: [
          { value: "1an", label: "Séparation d'au moins 1 an" },
          { value: "adultere", label: "Adultère" },
          { value: "cruaute", label: "Cruauté" },
        ],
      },
    ],
  },

  // Condition de résidence
  {
    id: "step1",
    phase: "preparation",
    title: "Condition de résidence (12 mois)",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Au Québec, un conjoint doit résider 12 mois avant la demande.",
    detailedContent: `La Cour supérieure du Québec est compétente si l'un des époux y habite depuis 12 mois.
      S'il manque des mois, vous devrez patienter avant de déposer votre demande (sauf si adultère ou cruauté).`,
    questions: [
      {
        id: "residence_12_months",
        label: "Avez-vous résidé au Québec 12 mois consécutifs ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
        help: "Si moins de 12 mois ET motif = adultère/cruauté, vous pouvez quand même procéder.",
      },
      {
        id: "missing_months",
        label: "Combien de mois vous manque-t-il ?",
        type: "text",
        help: "Exemple : '3'",
        showIf: (answers) => answers?.["step1"]?.residence_12_months === "non",
      },
    ],
  },

  // District judiciaire
  {
    id: "step_district",
    phase: "preparation",
    title: "Région / District judiciaire",
    minDuration: 0,
    maxDuration: 3,
    shortContent: "Sélectionnez la région où vous déposez votre demande.",
    detailedContent: `
      Selon la région, les rôles de la cour peuvent être plus ou moins engorgés. 
      À Montréal, par exemple, un procès complet peut prendre 12 à 24 mois. 
      Dans d'autres districts, 9 à 18 mois.
    `,
    questions: [
      {
        id: "district",
        label: "Quel est votre district judiciaire ?",
        options: [
          { value: "montreal", label: "Montréal" },
          { value: "autre", label: "Autre région du Québec" },
        ],
      },
    ],
  },

  // Présence d'enfants mineurs
  {
    id: "step_children",
    phase: "preparation",
    title: "Présence d'enfants mineurs",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Indiquez si des enfants mineurs sont concernés.",
    detailedContent: `Une séance d'information parentale peut être exigée s'il y a des enfants mineurs.`,
    questions: [
      {
        id: "have_children",
        label: "Avez-vous des enfants mineurs concernés ?",
        options: [
          { value: "non", label: "Non" },
          { value: "oui", label: "Oui" },
        ],
        help: "Sélectionnez Oui si applicable.",
      },
    ],
  },

  // Séance d'information parentale
  {
    id: "step_parentalite",
    phase: "preparation",
    title: "Séance d'information parentale",
    minDuration: 0.5,
    maxDuration: 1,
    shortContent: "Obligatoire en cas de conflit sur la garde.",
    detailedContent: `Cette séance informe sur les conséquences de la séparation et encourage la médiation familiale.`,
    questions: [
      {
        id: "parentalite_attestation",
        label: "Avez-vous déjà suivi cette séance ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
          { value: "dispense", label: "Dispensé(e)" },
        ],
        help: "Sélectionnez l'option correspondante.",
      },
    ],
    showIf: (answers) => answers["step_children"]?.have_children === "oui",
  },

  // Rassemblement des documents
  {
    id: "step2",
    phase: "preparation",
    title: "Rassemblement des documents",
    minDuration: DEFAULT_DOCS_MIN,
    maxDuration: DEFAULT_DOCS_MAX,
    shortContent:
      "Préparez les documents (acte de mariage, certificats, etc.).",
    detailedContent: (
      <div>
        <p>Rassemblez notamment :</p>
        <ul className="list-disc list-inside">
          <li>Certificat de mariage</li>
          <li>Certificats de naissance</li>
          <li>Preuves de revenus</li>
          <li>Relevés bancaires</li>
          <li>Preuves de résidence</li>
        </ul>
        <p>
          Pour un mariage à l'étranger, prévoyez du temps pour obtenir et
          traduire l'acte.
        </p>
        <p>
          <a href="#" className="underline text-blue-600">
            Divorce Checklist
          </a>
        </p>
      </div>
    ),
    questions: [
      {
        id: "docs_delay",
        label:
          "Durée prévue pour rassembler/traduire les documents (ex: '1' ou '2-3')",
        type: "text",
        help: "Laissez vide pour utiliser le délai par défaut.",
      },
    ],
  },

  // Expertise pension et Evaluation immo
  {
    id: "step_expertise",
    phase: "preparation",
    title: "Expertises financières (pension, immobilier)",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Fonds de pension ? Évaluation immobilière ?",
    detailedContent: `
      Si partage d'un fonds de pension (RREGOP...) par un actuaire => +3 à +6 mois. 
      Si évaluation immobilière => +1 à +3 mois pour le rapport.
    `,
    questions: [
      {
        id: "expertise_pension",
        label: "Besoin d'une expertise sur un fonds de pension ?",
        options: [
          { value: "non", label: "Non" },
          { value: "oui", label: "Oui (+3 à +6 mois)" },
        ],
      },
      {
        id: "eval_immo",
        label: "Besoin d'une évaluation immobilière ?",
        options: [
          { value: "non", label: "Non" },
          { value: "oui", label: "Oui (+1 à +3 mois)" },
        ],
      },
    ],
  },

  // Stratégie et urgences (mesures provisoires)
  {
    id: "step_strategies",
    phase: "preparation",
    title: "Stratégie et urgences",
    minDuration: 0,
    maxDuration: 1,
    shortContent: "Mesures provisoires possibles (garde, pension...).",
    detailedContent: (
      <div>
        <p>En cas d'urgence concernant :</p>
        <ul className="list-disc list-inside">
          <li>La garde des enfants</li>
          <li>La pension alimentaire</li>
          <li>L'occupation du domicile familial</li>
          <li>La protection des biens</li>
        </ul>
        <p className="mt-2">
          Des mesures provisoires peuvent être demandées pour obtenir une
          décision rapide du tribunal.
        </p>
      </div>
    ),
    questions: [
      {
        id: "mesures_provisoires",
        label:
          "Avez-vous besoin de mesures provisoires / ordonnance de sauvegarde ?",
        options: [
          { value: "non", label: "Non" },
          { value: "oui", label: "Oui (mesures provisoires urgentes)" },
        ],
        help:
          "Permet d'obtenir rapidement une décision sur des questions urgentes",
      },
    ],
  },

  // PHASE 2 : Processus judiciaire
  {
    id: "step3",
    phase: "judiciaire",
    title: "Rédaction et dépôt de la demande",
    minDuration: 0,
    maxDuration: 0,
    shortContent:
      "Rédigez et déposez votre demande à la Cour supérieure.",
    detailedContent: (
      <div>
        <p>Inclure :</p>
        <ul className="list-disc list-inside">
          <li>Vos informations et celles du conjoint</li>
          <li>Le motif du divorce</li>
          <li>Les demandes accessoires</li>
        </ul>
        <p>Frais de dépôt applicables.</p>
        <p>
          <strong>Délai habituel :</strong> ~1 mois.
        </p>
      </div>
    ),
    questions: [
      {
        id: "redaction_delay",
        label: "Durée prévue pour la rédaction (ex: '1')",
        type: "text",
        help: "Laissez vide pour ~1 mois par défaut.",
      },
    ],
  },

  {
    id: "step4",
    phase: "judiciaire",
    title: "Signification de la demande",
    minDuration: 0.25,
    maxDuration: 6,
    shortContent: "Signifiez la demande selon la localisation du conjoint.",
    detailedContent: (
      <div>
        <p>La procédure dépend de :</p>
        <ul className="list-disc list-inside mb-4">
          <li>L'adresse connue ou non</li>
          <li>Le lieu de résidence (Québec, Canada, étranger)</li>
        </ul>
        <div className="bg-blue-50 p-3 rounded-md">
          <p className="font-semibold">Adresse inconnue ?</p>
          <p className="text-sm">
            Possibilités :
          </p>
          <ul className="list-disc list-inside text-sm">
            <li>Mode spécial (autorisation du tribunal)</li>
            <li>Publication dans un journal</li>
            <li>Dépistage (Québec : 1-2 semaines, Canada : 2-4 semaines, International : 1-2 mois+)</li>
          </ul>
        </div>
      </div>
    ),
    questions: [
      {
        id: "adresse_exacte",
        label: "Connaissez-vous l'adresse exacte de l'autre partie ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
      },
      {
        id: "contacts_recents",
        label:
          "Possibilité d'obtenir l'adresse via famille, employeur, proches ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
        showIf: (answers) =>
          answers["step4"]?.adresse_exacte === "non",
      },
      {
        id: "localisation",
        label: "Où réside l'autre partie ?",
        options: [
          {
            value: "quebec",
            label: "Au Québec (1-2 semaines via huissier)",
          },
          {
            value: "canada",
            label: "Ailleurs au Canada (1-3 semaines)",
          },
          {
            value: "etranger",
            label: "À l'étranger",
          },
        ],
        showIf: (answers) =>
          answers["step4"]?.adresse_exacte === "oui" ||
          answers["step4"]?.contacts_recents === "oui",
      },
      {
        id: "pays_etranger",
        label: "Dans quel pays réside-t-il/elle ?",
        type: "select",
        options: allCountries.map((c) => ({
          value: c.code,
          label: `${c.name}${
            c.isSignatory
              ? " (Convention La Haye: 0.5-2 mois)"
              : " (Hors Convention: 1-6 mois)"
          }`,
        })),
        showIf: (answers) =>
          (answers["step4"]?.adresse_exacte === "oui" ||
            answers["step4"]?.contacts_recents === "oui") &&
          answers["step4"]?.localisation === "etranger",
      },
      {
        id: "contacts_electroniques",
        label:
          "Avez-vous des contacts récents (moins de 3 mois) par e-mail / réseaux sociaux ?",
        options: [
          {
            value: "oui",
            label: "Oui - Mode spécial possible (2-4 semaines)",
          },
          { value: "non", label: "Non" },
        ],
        showIf: (answers) =>
          answers["step4"]?.adresse_exacte === "non" &&
          answers["step4"]?.contacts_recents === "non",
      },
      {
        id: "residence_canada",
        label: "La personne a-t-elle déjà résidé au Canada ?",
        options: [
          {
            value: "oui",
            label: "Oui - Publication possible (2-3 semaines)",
          },
          { value: "non", label: "Non - Estimation incertaine" },
        ],
        showIf: (answers) =>
          answers["step4"]?.adresse_exacte === "non" &&
          answers["step4"]?.contacts_recents === "non" &&
          answers["step4"]?.contacts_electroniques === "non",
      },
      {
        id: "mode_special_decision",
        label: "Décision du juge sur le mode spécial",
        options: [
          { value: "accepte", label: "Accepté (1-2 semaines)" },
          { value: "refuse", label: "Refusé (Estimation impossible)" },
        ],
        showIf: (answers) =>
          answers["step4"]?.contacts_electroniques === "oui" ||
          answers["step4"]?.residence_canada === "oui",
      },
    ],
  },

  {
    id: "step4_unknownAddress",
    phase: "judiciaire",
    title: "Signification – Adresse inconnue",
    minDuration: 0.5,
    maxDuration: 1,
    shortContent: "Procédure spéciale si l'adresse du conjoint est inconnue.",
    detailedContent: `Effectuez un dépistage et demandez une signification spéciale.`,
    questions: [],
    showIf: (answers) => {
      // Example of a condition from older code; adapt if needed
      const s4 = answers["step4"] || {};
      return s4.q1 === "yes" && s4.q3 === "no";
    },
  },

  {
    id: "step_reponse",
    phase: "judiciaire",
    title: "Réponse / Défense du conjoint",
    minDuration: 0.5,
    maxDuration: 1,
    shortContent: "Le conjoint dispose de 15 ou 30 jours pour répondre.",
    detailedContent: `Selon sa localisation, le délai est de 15 jours (QC) ou 30 jours (hors QC).`,
    questions: [
      {
        id: "q_reponse_attendue",
        label: "Pensez-vous que votre conjoint va répondre ?",
        options: [
          { value: "non", label: "Non" },
          { value: "oui_accord", label: "Oui, accord" },
          { value: "oui_conflit", label: "Oui, contesté" },
        ],
      },
      {
        id: "q_delai_reponse",
        label: "Délai pour la réponse",
        options: (answers) => {
          // If mesures_provisoires => 3-10 days
          if (answers["step_strategies"]?.mesures_provisoires === "oui") {
            return [
              {
                value: "3-10",
                label: "3-10 jours (mesures provisoires)",
                disabled: true,
              },
            ];
          }
          const s4 = answers["step4"] || {};
          if (s4.adresse_exacte === "oui" || s4.contacts_recents === "oui") {
            const loc = s4.localisation;
            if (loc === "quebec") {
              return [
                { value: "15", label: "15 jours (QC)", disabled: true },
              ];
            } else if (loc === "canada" || loc === "etranger") {
              return [
                { value: "30", label: "30 jours (hors QC)", disabled: true },
              ];
            }
          }
          return [{ value: "15-30", label: "15-30 jours (délai incertain)" }];
        },
        showIf: (answers) =>
          answers["step_reponse"]?.q_reponse_attendue !== "non",
        help: "Délai déterminé en fonction des réponses précédentes.",
      },
    ],
    showIf: (answers) => {
      const s4 = answers["step4"] || {};
      // If absolutely no signification method, hide
      if (
        s4.residence_canada === "non" &&
        s4.contacts_electroniques === "non" &&
        s4.adresse_exacte === "non" &&
        s4.contacts_recents === "non"
      ) {
        return false;
      }
      // If mode spécial refusé => hide
      if (s4.mode_special_decision === "refuse") {
        return false;
      }
      return true;
    },
  },

  {
    id: "step_expertises",
    phase: "judiciaire",
    title: "Expertises psychosociales",
    minDuration: 0,
    maxDuration: 2,
    shortContent: "En cas de violence ou gros conflit, le juge peut ordonner une expertise.",
    detailedContent: `Une expertise psychosociale peut être requise en cas d'allégations sérieuses.`,
    questions: [
      {
        id: "q_exp_violence",
        label: "Y a-t-il des allégations de violence ou un dossier DPJ ?",
        options: [
          { value: "non", label: "Non" },
          { value: "oui", label: "Oui" },
        ],
      },
    ],
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
  },

  {
    id: "step_dpj",
    phase: "judiciaire",
    title: "Intervention de la DPJ",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "La DPJ peut intervenir en cas de maltraitance.",
    detailedContent: `Dans les cas graves, la DPJ peut influencer le jugement.`,
    questions: [],
    showIf: (answers) =>
      answers["step_expertises"]?.q_exp_violence === "oui",
  },

  {
    id: "step5",
    phase: "judiciaire",
    title: "Protocole d'instance / Médiation",
    minDuration: 3,
    maxDuration: 3,
    shortContent: "En cas de contestation, un protocole et une médiation sont mis en place.",
    detailedContent: `La médiation familiale et un protocole d'instance sont obligatoires en cas de conflit.`,
    questions: [
      {
        id: "q5_entente",
        label: "Pensez-vous parvenir à un accord rapidement ?",
        options: [
          { value: "oui", label: "Oui" },
          { value: "non", label: "Non" },
        ],
      },
    ],
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
  },

  {
    id: "step_consentement",
    phase: "judiciaire",
    title: "Consentement",
    minDuration: 0.25,
    maxDuration: 0.25,
    shortContent: "Un accord conclu peut être validé par le juge.",
    detailedContent: `Si un accord est trouvé, il suffit de le soumettre au juge.`,
    questions: [],
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_accord",
  },

  {
    id: "step6",
    phase: "judiciaire",
    title: "Poursuite de la contestation",
    minDuration: 1,
    maxDuration: 6,
    shortContent: "Si pas d'accord, la procédure avance vers un procès.",
    detailedContent: `Interrogatoires, échanges de pièces, etc. avant le procès.`,
    questions: [],
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
  },

  {
    id: "step_remises",
    phase: "judiciaire",
    title: "Remises et délais supplémentaires",
    minDuration: 0,
    maxDuration: 3,
    shortContent: "Des reports peuvent être demandés par les avocats.",
    detailedContent: `Une remise ajoute souvent plusieurs semaines ou mois.`,
    questions: [
      {
        id: "q_remise",
        label: "Pensez-vous demander un report ?",
        options: [
          { value: "non", label: "Non" },
          { value: "oui", label: "Oui" },
        ],
        help: "Sélectionnez Oui si un report semble probable.",
      },
    ],
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
  },

  {
    id: "step_delai_proces",
    phase: "judiciaire",
    title: "Délai avant le procès",
    minDuration: 12,
    maxDuration: 18,
    shortContent: "Le procès peut être fixé 12 à 18 mois plus tard.",
    detailedContent: `Variable selon la région et la complexité du dossier.`,
    questions: [],
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
  },

  {
    id: "step_reglement_avant",
    phase: "judiciaire",
    title: "Règlement avant le procès",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Un accord reste possible jusqu'au procès.",
    detailedContent: (
      <div>
        <p>
          Si un accord survient avant le procès, il sera validé par le juge.
        </p>
        <div className="bg-yellow-50 p-3 rounded mt-2">
          <p className="text-sm font-semibold">Important :</p>
          <p className="text-sm">
            En cas d'accord, vous pouvez relancer le questionnaire en choisissant
            "Oui, accord" à la question sur la réponse du conjoint.
          </p>
        </div>
      </div>
    ),
    questions: [
      {
        id: "q_reglement_avant",
        label: "Souhaitez-vous tenter un accord avant le procès ?",
        options: [
          { value: "oui", label: "Oui - Recommencer (procédure consentement)" },
          { value: "non", label: "Non - Continuer vers le procès" },
        ],
      },
    ],
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_conflit",
  },

  {
    id: "step7",
    phase: "judiciaire",
    title: "Procès",
    minDuration: 2,
    maxDuration: 6,
    shortContent: "Le procès peut durer de quelques heures à plusieurs jours.",
    detailedContent: `Le juge rendra sa décision, parfois après un délibéré.`,
    questions: [],
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "oui_conflit" &&
      answers["step_reglement_avant"]?.q_reglement_avant === "non",
  },

  {
    id: "step_default_judgement",
    phase: "judiciaire",
    title: "Jugement par défaut",
    minDuration: 1,
    maxDuration: 2,
    shortContent: "Si le conjoint ne répond pas, un jugement par défaut est prononcé.",
    detailedContent: `Après l'expiration du délai, le juge peut rendre jugement par défaut.`,
    questions: [],
    showIf: (answers) =>
      answers["step_reponse"]?.q_reponse_attendue === "non",
  },

  {
    id: "step8",
    phase: "judiciaire",
    title: "Jugement final & certificat",
    minDuration: 1,
    maxDuration: 7,
    shortContent: "Obtenir le jugement final et le certificat de divorce.",
    detailedContent: `Le juge peut prendre jusqu'à 6 mois pour rendre le jugement final, puis environ 1 mois pour le certificat.`,
    questions: [],
  },

  {
    id: "step_provisoires",
    phase: "judiciaire",
    title: "Procédure de mesures provisoires",
    minDuration: 0.25,
    maxDuration: 0.5,
    shortContent: "Procédure accélérée pour l'urgence.",
    detailedContent: (
      <div>
        <p className="font-semibold">Procédure accélérée :</p>
        <ul className="list-disc list-inside mb-2">
          <li>Délai de réponse réduit (3-10 jours)</li>
          <li>Audience rapide</li>
          <li>Décision temporaire en attendant le jugement final</li>
        </ul>
        <div className="bg-blue-50 p-2 rounded">
          <p className="text-sm">
            La décision provisoire n'affecte pas le fond du divorce.
          </p>
        </div>
      </div>
    ),
    questions: [
      {
        id: "type_urgence",
        label: "Type de mesures urgentes demandées",
        options: [
          { value: "garde", label: "Garde des enfants" },
          { value: "pension", label: "Pension alimentaire" },
          { value: "domicile", label: "Occupation du domicile" },
          { value: "multiple", label: "Plusieurs mesures" },
        ],
      },
    ],
    showIf: (answers) =>
      answers["step_strategies"]?.mesures_provisoires === "oui",
  },

  // PHASE 3 : Post-séparation
  {
    id: "step_post_separation",
    phase: "postSeparation",
    title: "Formalités post-divorce",
    minDuration: 0,
    maxDuration: 0,
    shortContent: "Mettez à jour vos documents (testament, assurances...).",
    detailedContent: (
      <div>
        <p>Après le divorce, n'oubliez pas :</p>
        <ul className="list-disc list-inside">
          <li>Mettre à jour vos documents officiels</li>
          <li>Modifier vos bénéficiaires d'assurance</li>
          <li>Réviser votre testament</li>
          <li>Informer banques et institutions</li>
        </ul>
        <p className="mt-2 text-xs text-gray-600">
          Note : Ces formalités n'affectent pas la durée du divorce.
        </p>
      </div>
    ),
    questions: [],
  },
];

// -----------------------------------------------------------------------------
// We'll continue in Part 2...
// -----------------------------------------------------------------------------

// (STOP HERE for Part 1 - Next answer will include the rest: 
//  calculation functions + the main component's export.)

// -----------------------------------------------------------------------------
// Continue from Part 1: Calculations + Main Component
// -----------------------------------------------------------------------------

// Fallback constant for drafting the demand
const DEFAULT_REDACTION = 1;

/**
 * Calculate signification delay based on user answers
 * @param {object} answers
 * @returns {{min: number, max: number, msg: string}}
 */
function calculateSignificationDelay(answers) {
  const s4 = answers["step4"] || {};

  // If "mesures provisoires" => urgent signification
  if (answers["step_strategies"]?.mesures_provisoires === "oui") {
    return {
      min: 0.1, // ~3 days in months
      max: 0.3, // ~10 days in months
      msg: "[Signification] Urgente (mesures provisoires): 3-10 jours",
    };
  }

  // If address known directly or via contacts
  if (s4.adresse_exacte === "oui" || s4.contacts_recents === "oui") {
    switch (s4.localisation) {
      case "quebec":
        return {
          min: 0.25,
          max: 0.5,
          msg: "[Signification] Québec: 1-2 semaines",
        };
      case "canada":
        return {
          min: 0.25,
          max: 0.75,
          msg: "[Signification] Canada (hors QC): 1-3 semaines",
        };
      case "etranger":
        // If a specific country was selected
        const country = allCountries.find((c) => c.code === s4.pays_etranger);
        if (country?.isSignatory) {
          return {
            min: 0.5,
            max: 2,
            msg: `[Signification] ${country.name} (Convention La Haye): 0.5-2 mois`,
          };
        }
        // Non-Hague
        return {
          min: 1,
          max: 6,
          msg: `[Signification] ${country?.name || "Étranger"} (Hors Convention): 1-6 mois`,
        };
      default:
        return {
          min: 0.5,
          max: 1,
          msg: "[Signification] Québec (défaut)",
        };
    }
  }

  // If "mode spécial" or "publication"...
  if (s4.contacts_electroniques === "oui" || s4.residence_canada === "oui") {
    if (s4.mode_special_decision === "accepte") {
      return {
        min: 0.25,
        max: 0.5,
        msg: "[Signification] Mode spécial autorisé: 1-2 semaines",
      };
    }
    if (s4.mode_special_decision === "refuse") {
      return {
        min: 0.5,
        max: 1,
        msg: "[Signification] Mode spécial refusé => +2-4 semaines",
      };
    }
    // Still waiting for judge's decision
    return {
      min: 0.5,
      max: 1,
      msg: "[Signification] En attente d'autorisation du tribunal",
    };
  }

  // Complex unknown address scenario
  return {
    min: 2,
    max: 4,
    msg: "[Signification] Complexe - consultation avocat recommandée",
  };
}

/**
 * The main function to calculate timeline (min/max months).
 * Also collects "phaseHistory" messages to display details.
 * @param {object} answers
 * @param {string[]} completedSteps
 */
function calculateTimeline(answers, completedSteps) {
  // We'll track each phase's steps for final display
  const phaseHistory = {
    preparation: [],
    judiciaire: [],
    postSeparation: [],
  };

  let totalMin = 0;
  let totalMax = 0;

  // Filter steps that are actually visible given showIf conditions
  const visibleSteps = stepsData.filter((step) => {
    if (typeof step.showIf === "function" && step.showIf(answers) === false) {
      return false;
    }
    return true;
  });

  // Helper to compute each step's min/max
  function calcStepDelay(step) {
    // If user has NOT completed that step => zero for now
    if (!completedSteps.includes(step.id)) {
      return { min: 0, max: 0 };
    }

    // For step2 (docs) - allow custom input
    if (step.id === "step2") {
      const userVal = parseMonths(answers["step2"]?.docs_delay);
      if (userVal > 0) {
        phaseHistory[step.phase].push(`[${step.title}] Délai perso: ${userVal} mois`);
        return { min: userVal, max: userVal };
      }
      phaseHistory[step.phase].push(
        `[${step.title}] Délai par défaut: ${DEFAULT_DOCS_MIN}-${DEFAULT_DOCS_MAX} mois`
      );
      return { min: DEFAULT_DOCS_MIN, max: DEFAULT_DOCS_MAX };
    }

    // For step3 (redaction) - allow custom input
    if (step.id === "step3") {
      const redacDelay = parseMonths(answers["step3"]?.redaction_delay);
      if (redacDelay > 0) {
        phaseHistory[step.phase].push(`[${step.title}] Délai perso: ${redacDelay} mois`);
        return { min: redacDelay, max: redacDelay };
      }
      phaseHistory[step.phase].push(`[${step.title}] Délai par défaut: ~${DEFAULT_REDACTION} mois`);
      return { min: DEFAULT_REDACTION, max: DEFAULT_REDACTION };
    }

    // For step4 (signification)
    if (step.id === "step4") {
      const signi = calculateSignificationDelay(answers);
      phaseHistory.judiciaire.push(signi.msg);
      return { min: signi.min, max: signi.max };
    }

    // For step_reponse
    if (step.id === "step_reponse") {
      const rep = answers["step_reponse"] || {};
      let minDelay = 0.5;
      let maxDelay = 1;

      if (rep.q_reponse_attendue === "non") {
        phaseHistory.judiciaire.push("[Réponse] Pas de défense => 0.5-1 mois");
      } else if (rep.q_reponse_attendue === "oui_accord") {
        minDelay = 0.25;
        maxDelay = 0.5;
        phaseHistory.judiciaire.push("[Réponse] Accord mutuel => 1-2 semaines");
      } else {
        // yes_conflict
        if (rep.q_delai_reponse === "30") {
          minDelay = 1;
          maxDelay = 1.5;
          phaseHistory.judiciaire.push("[Réponse] Hors QC => 1-1.5 mois");
        } else {
          minDelay = 0.5;
          maxDelay = 0.75;
          phaseHistory.judiciaire.push("[Réponse] Québec => 0.5-0.75 mois");
        }
      }
      return { min: minDelay, max: maxDelay };
    }

    // For step_consentement
    if (step.id === "step_consentement") {
      phaseHistory.judiciaire.push(`[${step.title}] Accord: ${step.minDuration} mois`);
      return { min: step.minDuration, max: step.maxDuration };
    }

    // Default: just use step.minDuration / maxDuration
    phaseHistory[step.phase].push(
      `[${step.title}] Durée: ${step.minDuration}-${step.maxDuration} mois`
    );
    return {
      min: step.minDuration,
      max: step.maxDuration,
    };
  }

  // Loop through all visible steps
  visibleSteps.forEach((step) => {
    const { min, max } = calcStepDelay(step);
    totalMin += min;
    totalMax += max;
  });

  // Additional logic beyond single-step durations
  // (motif, missing residence months, expertise, etc.)

  // 1) Condition de résidence & motif adultère/cruauté
  const st1 = answers["step1"] || {};
  const stMotif = answers["step_motif_divorce"] || {};
  if (completedSteps.includes("step1")) {
    if (st1.residence_12_months === "non") {
      // If NOT adultère/cruauté => must wait missing months
      if (
        stMotif.motif_divorce !== "adultere" &&
        stMotif.motif_divorce !== "cruaute"
      ) {
        const missing = parseMonths(st1.missing_months);
        if (missing > 0) {
          totalMin += missing;
          totalMax += missing;
          phaseHistory.preparation.push(`[Resid. manquante] +${missing} mois`);
        }
      } else {
        phaseHistory.preparation.push(
          "[Resid. 12 mois] Motif adultère/cruauté => pas d'attente supplémentaire"
        );
      }
    }
  }

  // 2) Motif adultère/cruauté => extra delay for proof
  if (
    stMotif.motif_divorce === "adultere" ||
    stMotif.motif_divorce === "cruaute"
  ) {
    totalMin += ADULTERE_CRUAUTE_EXTRA_MIN; // +1
    totalMax += ADULTERE_CRUAUTE_EXTRA_MAX; // +4
    phaseHistory.judiciaire.push(
      `[Motif adultère/cruauté] +${ADULTERE_CRUAUTE_EXTRA_MIN}-${ADULTERE_CRUAUTE_EXTRA_MAX} mois de preuve`
    );
  }

  // 3) District judiciaire => Info only
  const stDistrict = answers["step_district"] || {};
  if (completedSteps.includes("step_delai_proces")) {
    if (stDistrict.district === "montreal") {
      phaseHistory.judiciaire.push(
        "Note: À Montréal, le procès peut atteindre 24 mois au total"
      );
    } else if (stDistrict.district === "autre") {
      phaseHistory.judiciaire.push(
        "Note: En régions, le procès peut se situer entre 9-18 mois"
      );
    }
  }

  // 4) Expertise pension & immo
  const stExpert = answers["step_expertise"] || {};
  if (completedSteps.includes("step_expertise")) {
    if (stExpert.expertise_pension === "oui") {
      totalMin += EXPERTISE_PENSION_MIN; // +3
      totalMax += EXPERTISE_PENSION_MAX; // +6
      phaseHistory.preparation.push(
        `[Expertise Pension] +${EXPERTISE_PENSION_MIN}-${EXPERTISE_PENSION_MAX} mois`
      );
    }
    if (stExpert.eval_immo === "oui") {
      totalMin += EVAL_IMMO_MIN; // +1
      totalMax += EVAL_IMMO_MAX; // +3
      phaseHistory.preparation.push(
        `[Évaluation Immo] +${EVAL_IMMO_MIN}-${EVAL_IMMO_MAX} mois`
      );
    }
  }

  // 5) Remises
  const stRem = answers["step_remises"] || {};
  if (stRem.q_remise === "oui") {
    totalMin += 1;
    totalMax += 1;
    phaseHistory.judiciaire.push("[Remises] +1 mois");
  }

  // 6) Mesures provisoires => quick hearing
  if (
    answers["step_strategies"]?.mesures_provisoires === "oui" &&
    completedSteps.includes("step_provisoires")
  ) {
    totalMin += 0.25;
    totalMax += 0.5;
    phaseHistory.judiciaire.push(
      "[Mesures provisoires] Décision rapide: 1-2 semaines"
    );
  }

  return { totalMin, totalMax, phaseHistory };
}

// -----------------------------------------------------------------------------
// MAIN COMPONENT
// -----------------------------------------------------------------------------

export default function DivorceTimeline() {
  const { t, i18n } = useTranslation(['timeline', 'common']);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [openStep, setOpenStep] = useState(null);
  const [answers, setAnswers] = useState({});
  const [timelineSummary, setTimelineSummary] = useState({
    totalMin: 0,
    totalMax: 0,
    phaseHistory: {
      preparation: [],
      judiciaire: [],
      postSeparation: [],
    },
  });

  // Force component update when language changes
  useEffect(() => {
    // This empty dependency array with i18n.language will trigger a re-render when language changes
  }, [i18n.language]);

  // Recalculate timeline whenever answers or completedSteps change
  useEffect(() => {
    setTimelineSummary(calculateTimeline(answers, completedSteps));

    // Auto-set q_delai_reponse based on user data
    const s4 = answers["step4"] || {};
    if (answers["step_strategies"]?.mesures_provisoires === "oui") {
      setAnswers((prev) => ({
        ...prev,
        step_reponse: {
          ...prev.step_reponse,
          q_delai_reponse: "3-10",
        },
      }));
    } else if (s4.adresse_exacte === "oui" || s4.contacts_recents === "oui") {
      if (s4.localisation === "quebec") {
        setAnswers((prev) => ({
          ...prev,
          step_reponse: {
            ...prev.step_reponse,
            q_delai_reponse: "15",
          },
        }));
      } else if (s4.localisation === "canada" || s4.localisation === "etranger") {
        setAnswers((prev) => ({
          ...prev,
          step_reponse: {
            ...prev.step_reponse,
            q_delai_reponse: "30",
          },
        }));
      }
    }
  }, [answers, completedSteps]);

  /**
   * Handle user answering a question for a given step
   * @param {string} stepId
   * @param {string} questionId
   * @param {any} value
   */
  function handleAnswerChange(stepId, questionId, value) {
    setAnswers((prev) => ({
      ...prev,
      [stepId]: {
        ...prev[stepId],
        [questionId]: value,
      },
    }));
  }

  /**
   * Check if all questions for a step are answered (if they're visible)
   * @param {object} step
   * @returns {boolean}
   */
  function areAllQuestionsAnswered(step) {
    if (!step.questions?.length) return true;
    const stepAns = answers[step.id] || {};
    return step.questions.every((q) => {
      if (typeof q.showIf === "function" && !q.showIf(answers)) return true;
      // docs_delay/redaction_delay can remain empty if user wants default
      if (["docs_delay", "redaction_delay"].includes(q.id)) return true;
      return !!stepAns[q.id];
    });
  }

  /**
   * Mark a step as "done" (completed), if all required questions are answered
   * Also ensures previous steps are done in order.
   * @param {object} step
   */
  function markStepAsDone(step) {
    if (!areAllQuestionsAnswered(step)) {
      alert(t('timeline:divorceTimeline.alerts.completeRequired'));
      return;
    }

    // Ensure previous steps are completed
    const visibleSteps = stepsData.filter(
      (s) => !s.showIf || s.showIf(answers) !== false
    );
    const currentIndex = visibleSteps.findIndex((s) => s.id === step.id);
    const prevNotDone = visibleSteps
      .slice(0, currentIndex)
      .some((s) => !completedSteps.includes(s.id));
    if (prevNotDone) {
      alert(t('timeline:divorceTimeline.alerts.completePrevious'));
      return;
    }

    if (window.confirm(t('timeline:divorceTimeline.alerts.confirmStep', { stepTitle: step.title }))) {
      if (!completedSteps.includes(step.id)) {
        setCompletedSteps((prev) => [...prev, step.id]);
      }
      setOpenStep(null);
    }

    // If user chooses to "Recommencer" for a settlement
    if (
      step.id === "step_reglement_avant" &&
      answers[step.id]?.q_reglement_avant === "oui"
    ) {
      if (
        window.confirm(
          "Pour passer à la procédure par consentement, voulez-vous réinitialiser le questionnaire ?"
        )
      ) {
        resetAll();
      }
    }
  }

  function resetAll() {
    if (window.confirm(t('timeline:divorceTimeline.alerts.confirmReset'))) {
      setCompletedSteps([]);
      setOpenStep(null);
      setAnswers({});
      setTimelineSummary({
        totalMin: 0,
        totalMax: 0,
        phaseHistory: {
          preparation: [],
          judiciaire: [],
          postSeparation: [],
        },
      });
    }
  }

  // For the "progress bar" target: sum up the minimal durations
  function getPlannedStepDelay(step) {
    if (step.id === "step2") {
      const val = parseMonths(answers["step2"]?.docs_delay);
      return val > 0 ? val : DEFAULT_DOCS_MIN;
    }
    if (step.id === "step3") {
      const val = parseMonths(answers["step3"]?.redaction_delay);
      return val > 0 ? val : DEFAULT_REDACTION;
    }
    return step.minDuration;
  }

  // Filter visible steps
  const visibleSteps = stepsData.filter(
    (s) => !s.showIf || s.showIf(answers) !== false
  );

  // Sum up minimal durations to define "plannedTotal"
  let plannedTotal = visibleSteps.reduce((acc, s) => acc + getPlannedStepDelay(s), 0);

  // Residence shortfall if no adultère/cruauté
  const st1 = answers["step1"] || {};
  const stMotif = answers["step_motif_divorce"] || {};
  if (st1.residence_12_months === "non") {
    if (stMotif.motif_divorce !== "adultere" && stMotif.motif_divorce !== "cruaute") {
      plannedTotal += parseMonths(st1.missing_months || "0");
    }
  }

  // If a "remise" is expected => +1
  const stRem = answers["step_remises"] || {};
  if (stRem.q_remise === "oui") {
    plannedTotal += 1;
  }

  const { totalMin, totalMax, phaseHistory } = timelineSummary;
  const progressPercent = plannedTotal
    ? Math.min(Math.round((totalMin / plannedTotal) * 100), 100)
    : 0;

  // Prepare a list of phases for grouping steps
  const PHASES = [
    { id: "preparation", label: t('timeline:divorceTimeline.phases.preparation.title') },
    { id: "judiciaire", label: t('timeline:divorceTimeline.phases.judicial.title') },
    { id: "postSeparation", label: t('timeline:divorceTimeline.phases.postDivorce.title') },
  ];

  // Colors for styling, adapt as needed
  const COLORS = {
    phases: {
      preparation: "bg-blue-100 border-blue-200 text-blue-700",
      judiciaire: "bg-amber-100 border-amber-200 text-amber-700",
      postSeparation: "bg-green-100 border-green-200 text-green-700",
    },
  };

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="px-6 py-8 mt-4 bg-blue-50 rounded-xl border border-blue-100 shadow-sm mb-8">
        <h1 className="text-2xl font-bold text-blue-700 mb-3 text-center">
          <span className="inline-block h-1 bg-warning w-8 mr-2 rounded-full align-middle"></span>
          {t('timeline:divorceTimeline.header.title')}
          <span className="inline-block h-1 bg-warning w-8 ml-2 rounded-full align-middle"></span>
        </h1>
        <p className="text-center text-blue-600 max-w-3xl mx-auto">
          {t('timeline:divorceTimeline.header.subtitle')}
        </p>
      </div>

      {/* Top 3 cards: progression, estimation, actions */}
      <div className="grid md:grid-cols-3 gap-6 mb-8 px-4">
        {/* Card 1: Progression */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">📊</span>
            {t('timeline:divorceTimeline.interface.cards.progress.title')}
          </h3>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span className="font-semibold">{progressPercent}% {t('timeline:divorceTimeline.interface.cards.progress.completed')}</span>
              <span className="text-slate-500 text-xs">
                {totalMin.toFixed(1)} / {plannedTotal.toFixed(1)} {t('timeline:divorceTimeline.interface.cards.progress.months')}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-3">
              <div
                className="bg-blue-500 h-3 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progressPercent}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Card 2: Estimation */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">⏱️</span>
            {t('timeline:divorceTimeline.interface.cards.estimation.title')}
          </h3>
          <div className="flex items-center justify-center h-16">
            {totalMin === 0 && totalMax === 0 ? (
              <p className="text-slate-500 text-center text-sm">
                {t('timeline:divorceTimeline.interface.cards.estimation.completeStepsPrompt')}
              </p>
            ) : (
              <div className="text-center">
                <span className="text-2xl font-bold text-blue-700">
                  {totalMin} {t('timeline:divorceTimeline.interface.cards.estimation.to')} {totalMax} {t('timeline:divorceTimeline.interface.cards.estimation.months')}
                </span>
                <p className="text-xs text-slate-500 mt-1">{t('timeline:divorceTimeline.interface.cards.estimation.basedOnAnswers')}</p>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Actions */}
        <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
          <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
            <span className="bg-blue-50 p-2 rounded-full mr-2">🔄</span>
            {t('timeline:divorceTimeline.interface.cards.actions.title')}
          </h3>
          <div className="flex justify-center items-center h-16">
            <button
              onClick={resetAll}
              className="btn btn-outline border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400"
            >
              {t('timeline:divorceTimeline.interface.cards.actions.reset')}
            </button>
          </div>
        </div>
      </div>

      {/* Phase History / Detailed calculation */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-8 mx-4">
        <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
          <span className="bg-blue-50 p-2 rounded-full mr-2">📋</span>
          {t('timeline:divorceTimeline.interface.details.title')}
        </h3>
        {Object.entries(phaseHistory).some(([_, list]) => list.length > 0) ? (
          <div className="space-y-4">
            {Object.entries(phaseHistory).map(([phase, msgs]) => {
              if (!msgs.length) return null;
              return (
                <div key={phase} className={`p-3 rounded-lg ${COLORS.phases[phase]}`}>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <span className="mr-2">
                      {phase === "preparation" ? "📋" : phase === "judiciaire" ? "⚖️" : "📝"}
                    </span>
                    {phase === "preparation"
                      ? t('timeline:divorceTimeline.phases.preparation.title')
                      : phase === "judiciaire"
                      ? t('timeline:divorceTimeline.phases.judicial.title')
                      : t('timeline:divorceTimeline.phases.postDivorce.title')}
                  </h4>
                  <ul className="list-disc list-inside text-sm space-y-1">
                    {msgs.map((m, idx) => (
                      <li key={idx} className="text-slate-700">
                        {m}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center p-4 text-slate-500">
            <p>{t('timeline:divorceTimeline.interface.details.noData')}</p>
          </div>
        )}
      </div>

      {/* Steps grouped by phase with an "accordion" style */}
      <div className="space-y-6 px-4 mb-8">
        {PHASES.map((phaseObj) => {
          const stepsInPhase = visibleSteps.filter((s) => s.phase === phaseObj.id);
          if (!stepsInPhase.length) return null;

          return (
            <div key={phaseObj.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              {/* Phase header */}
              <div className={`p-4 ${COLORS.phases[phaseObj.id]}`}>
                <h2 className="text-lg font-bold flex items-center">
                  <span className="mr-3 text-2xl">
                    {phaseObj.id === "preparation" ? "📋" : phaseObj.id === "judiciaire" ? "⚖️" : "📝"}
                  </span>
                  {phaseObj.label}
                </h2>
              </div>

              {/* Steps inside this phase */}
              <div className="p-4 relative">
                {/* Decorative vertical line */}
                <div className="absolute w-[4px] h-full left-6 top-0 rounded-full z-0 bg-gradient-to-b from-blue-100 via-amber-100 to-green-100"></div>

                <div className="space-y-8">
                  {stepsInPhase.map((step, idx) => {
                    const globalIndex = stepsData.findIndex((x) => x.id === step.id);
                    const isDone = completedSteps.includes(step.id);
                    const isOpen = openStep === step.id;
                    const isActive = isOpen && !isDone;

                    // Style changes depending on step status
                    let style = {
                      bg: "bg-white",
                      border: "border-slate-200",
                      text: "text-slate-700",
                      bulletBg: "bg-white",
                      bulletBorder: "border-slate-300",
                      bulletText: "text-slate-700",
                    };

                    if (isDone) {
                      style = {
                        bg: "bg-green-50",
                        border: "border-green-200",
                        text: "text-green-700",
                        bulletBg: "bg-green-500",
                        bulletBorder: "border-green-500",
                        bulletText: "text-white",
                      };
                    } else if (isActive) {
                      style = {
                        bg: "bg-amber-50",
                        border: "border-amber-200",
                        text: "text-amber-700",
                        bulletBg: "bg-amber-50",
                        bulletBorder: "border-amber-500",
                        bulletText: "text-amber-700",
                      };
                    }

                    return (
                      <div key={step.id} className="flex flex-col md:flex-row relative z-10">
                        {/* Timeline bullet */}
                        <div className="mr-6">
                          <div
                            className={`w-12 h-12 rounded-full border-4 shadow-sm flex items-center justify-center text-lg font-bold 
                              ${style.bulletBg} ${style.bulletBorder} ${style.bulletText}
                              transition-all duration-300 ease-in-out
                              ${isDone ? "scale-110" : ""}
                            `}
                            style={{ marginLeft: "-4px" }}
                          >
                            {isDone ? "✓" : globalIndex + 1}
                          </div>
                        </div>

                        {/* Step content card */}
                        <div className="flex-1 pt-1">
                          <div
                            className={`card w-full border ${style.border} ${style.bg} rounded-xl shadow-sm
                            hover:shadow-md transition-all duration-200 ease-in-out
                            ${isActive ? "transform scale-[1.02]" : ""}
                            `}
                          >
                            <div className="card-body p-5">
                              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                                <h3 className={`card-title text-lg ${style.text}`}>
                                  {step.title}
                                </h3>
                              </div>
                              <p className="mt-3 text-slate-600">
                                {step.shortContent}
                              </p>

                              {/* Buttons */}
                              <div className="mt-4 flex gap-3 flex-wrap">
                                <button
                                  className={`btn btn-sm rounded-full px-4 ${
                                    isOpen
                                      ? "bg-amber-100 border-amber-200 text-amber-700 hover:bg-amber-200"
                                      : "bg-blue-100 border-blue-200 text-blue-700 hover:bg-blue-200"
                                  }`}
                                  onClick={() => setOpenStep(isOpen ? null : step.id)}
                                >
                                  {isOpen ? (
                                    <>
                                      {t('timeline:divorceTimeline.steps.actions.hide')}
                                      <span className="ml-1">▲</span>
                                    </>
                                  ) : (
                                    <>
                                      {t('timeline:divorceTimeline.steps.actions.details')}
                                      <span className="ml-1">▼</span>
                                    </>
                                  )}
                                </button>

                                {!isDone && (
                                  <button
                                    className="btn btn-sm rounded-full px-4 bg-green-100 border-green-200 text-green-700 hover:bg-green-200"
                                    onClick={() => markStepAsDone(step)}
                                  >
                                    ✓ {t('timeline:divorceTimeline.steps.actions.complete')}
                                  </button>
                                )}

                                {isDone && (
                                  <span
                                    className="text-green-600 bg-green-50 px-3 py-1 rounded-full text-sm font-semibold border border-green-100 self-center"
                                  >
                                    {t('timeline:divorceTimeline.steps.status.completed')}
                                  </span>
                                )}
                              </div>

                              {/* Accordion: Detailed content + questions */}
                              {isOpen && (
                                <div className="mt-5 animate-[fadeIn_0.3s_ease-in-out]">
                                  {/* Temporary fade-in keyframes (remove if needed) */}
                                  <style>{`
                                    @keyframes fadeIn {
                                      from { opacity: 0; transform: translateY(-10px); }
                                      to { opacity: 1; transform: translateY(0); }
                                    }
                                  `}</style>

                                  {/* Detailed explanation */}
                                  <div className="p-4 border border-slate-200 rounded-lg bg-white text-slate-600 mb-4">
                                    <h4 className="font-semibold text-blue-700 mb-2">Détails de l'étape</h4>
                                    {typeof step.detailedContent === "string" ? (
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: step.detailedContent,
                                        }}
                                      />
                                    ) : (
                                      step.detailedContent
                                    )}
                                  </div>

                                  {/* Questions */}
                                  {step.questions?.length > 0 && (
                                    <div className="p-4 border border-amber-200 bg-amber-50 rounded-lg">
                                      <h4 className="font-semibold text-amber-700 mb-3 flex items-center">
                                        <span className="mr-2">❓</span>
                                        Questions pour adapter l'estimation
                                      </h4>
                                      <div className="space-y-5">
                                        {step.questions.map((q) => {
                                          // If showIf is false => skip
                                          if (typeof q.showIf === "function" && !q.showIf(answers)) {
                                            return null;
                                          }
                                          const stepAns = answers[step.id] || {};
                                          const selectedValue = stepAns[q.id] || "";

                                          // If type text / number
                                          if (q.type === "text" || q.type === "number") {
                                            return (
                                              <div
                                                key={q.id}
                                                className="bg-white p-3 rounded-lg border border-amber-100"
                                              >
                                                <label className="font-semibold block mb-1 text-blue-700">
                                                  {q.label}
                                                </label>
                                                {q.help && (
                                                  <p className="text-xs text-slate-500 mb-3">
                                                    {q.help}
                                                  </p>
                                                )}
                                                <input
                                                  type={q.type}
                                                  className="input input-bordered w-full max-w-md border-slate-300 focus:border-blue-400 focus:ring-blue-200"
                                                  value={selectedValue}
                                                  onChange={(e) =>
                                                    handleAnswerChange(
                                                      step.id,
                                                      q.id,
                                                      e.target.value
                                                    )
                                                  }
                                                  placeholder={q.type === "text" ? "Votre réponse..." : "0"}
                                                />
                                              </div>
                                            );
                                          }

                                          // If type select
                                          if (q.type === "select") {
                                            return (
                                              <div
                                                key={q.id}
                                                className="bg-white p-3 rounded-lg border border-amber-100"
                                              >
                                                <label className="font-semibold block mb-1 text-blue-700">
                                                  {q.label}
                                                </label>
                                                {q.help && (
                                                  <p className="text-xs text-slate-500 mb-3">
                                                    {q.help}
                                                  </p>
                                                )}
                                                <select
                                                  className="select select-bordered w-full max-w-md border-slate-300 focus:border-blue-400 focus:ring-blue-200"
                                                  value={selectedValue}
                                                  onChange={(e) =>
                                                    handleAnswerChange(
                                                      step.id,
                                                      q.id,
                                                      e.target.value
                                                    )
                                                  }
                                                >
                                                  <option value="">
                                                    -- Sélectionnez une option --
                                                  </option>
                                                  {q.options?.map((opt) => (
                                                    <option key={opt.value} value={opt.value}>
                                                      {opt.label}
                                                    </option>
                                                  ))}
                                                </select>
                                              </div>
                                            );
                                          }

                                          // Radio-like options with no type
                                          if (q.options && !q.type) {
                                            // Possibly dynamic
                                            const ops =
                                              typeof q.options === "function"
                                                ? q.options(answers)
                                                : q.options;
                                            return (
                                              <div
                                                key={q.id}
                                                className="bg-white p-3 rounded-lg border border-amber-100"
                                              >
                                                <label className="font-semibold block mb-1 text-blue-700">
                                                  {q.label}
                                                </label>
                                                {q.help && (
                                                  <p className="text-xs text-slate-500 mb-3">
                                                    {q.help}
                                                  </p>
                                                )}
                                                <div className="space-y-2 mt-3">
                                                  {ops.map((opt) => (
                                                    <label
                                                      key={opt.value}
                                                      className={`flex items-center gap-2 p-2 hover:bg-blue-50 rounded-md transition-colors
                                                        ${
                                                          selectedValue === opt.value
                                                            ? "bg-blue-50 border border-blue-100"
                                                            : ""
                                                        }
                                                        ${
                                                          opt.disabled
                                                            ? "opacity-50 cursor-not-allowed"
                                                            : "cursor-pointer"
                                                        }
                                                      `}
                                                    >
                                                      <input
                                                        type="radio"
                                                        className="radio radio-sm radio-primary"
                                                        name={`${step.id}-${q.id}`}
                                                        value={opt.value}
                                                        checked={selectedValue === opt.value}
                                                        onChange={() => {
                                                          if (!opt.disabled) {
                                                            handleAnswerChange(step.id, q.id, opt.value);
                                                          }
                                                        }}
                                                        disabled={opt.disabled}
                                                      />
                                                      <span>{opt.label}</span>
                                                    </label>
                                                  ))}
                                                </div>
                                              </div>
                                            );
                                          }

                                          return null;
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legal note */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm text-slate-600 mb-8 mx-4">
        <p className="italic text-center">
          {t('timeline:divorceTimeline.legalNote')}
        </p>
      </div>
    </div>
  );
}